import {
  IMessage,
  IUserId,
  IVisibleTo,
  sortRecipientsByNameAlphabetically,
} from "@home-health-notify/domain";
import {
  hasRecipientsChanged,
  IChatInfoAccess,
  IOrganizationContext,
  OrganizationContext,
  UserDirectoryContext,
  useTranslation,
} from "hhn-commons";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import EmptyRecordView from "../../../common/EmptyRecordView";
import MessagePanelItem from "../../messagePanelItem/MessagePanelItem";
import {
  Loader,
  MessageItemModalContentWrapper,
  MessagePanelItemWrapper,
  MessageStatusListHeader,
} from "../MessagePanel.style";
import MessageDetailSearchBox from "./MessageDetailSearchBox";
import MessageForwardedInfoContent from "./MessageForwardedInfoContent";
import RecipientList from "./RecipientList";

interface MessageDetailProps {
  message: IMessage;
  recipients: IVisibleTo[];
  isLoading: boolean;
  showSearch?: boolean;
  enableAcknowledgement?: boolean;
  setSelectedUsers?: Dispatch<SetStateAction<IUserId[]>>;
  selectedUsers?: IUserId[];
  chatInfoAccess: IChatInfoAccess;
}

const MessageDetail = ({
  message,
  recipients,
  isLoading,
  showSearch = false,
  enableAcknowledgement = false,
  setSelectedUsers,
  selectedUsers = [],
  chatInfoAccess,
}: MessageDetailProps) => {
  const { selectedOrgUser }: IOrganizationContext =
    useContext(OrganizationContext);
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "chat" });

  const { userDirectory } = useContext(UserDirectoryContext);
  const [filteredRecipientList, setFilteredRecipientList] = useState<
    IVisibleTo[]
  >(sortRecipientsByNameAlphabetically(recipients, userDirectory));
  const handleUserSelected = (userId: IUserId) => {
    if (setSelectedUsers) {
      setSelectedUsers((prevSelectedUsers: IUserId[]) => {
        if (prevSelectedUsers.includes(userId)) {
          return prevSelectedUsers.filter(id => id !== userId);
        } else {
          return [...prevSelectedUsers, userId];
        }
      });
    }
  };
  useEffect(() => {
    const sortedRecipients = [...recipients].sort((a, b) => {
      const userA = userDirectory.getUserInfo(a.id);
      const userB = userDirectory.getUserInfo(b.id);
      return userA.name.localeCompare(userB.name);
    });
    setFilteredRecipientList(prevFiltered => {
      return hasRecipientsChanged(prevFiltered, sortedRecipients)
        ? sortedRecipients
        : prevFiltered;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipients]);

  const emptyRecordMessage = enableAcknowledgement
    ? tNs("askForAcknowledgement.noRecipient")
    : t("emptyRecordMessage");

  const UnSupported = () => {
    throw new Error(
      "Panic: message selection not allowed for messages displayed on Info/Ack action",
    );
  };
  return (
    <MessageItemModalContentWrapper data-testid="message-detail-recipient-list">
      <MessagePanelItemWrapper>
        {selectedOrgUser && (
          <MessagePanelItem
            selectedOrgUser={selectedOrgUser}
            message={message}
            showMessageInfo
            disableActions
            chatInfoAccess={chatInfoAccess}
            selectMessage={UnSupported}
            hideContextMenu={true}
            isReplyPreview={true}
          />
        )}
      </MessagePanelItemWrapper>
      {!enableAcknowledgement && message.getMessage().isForwarded && (
        <MessageForwardedInfoContent message={message} />
      )}
      <MessageDetailSearchBox
        showSearch={showSearch}
        recipients={recipients}
        setFilteredRecipientList={setFilteredRecipientList}
      />
      {!enableAcknowledgement && (
        <MessageStatusListHeader gutterBottom>
          {tNs("messageInfo.messageStatusListHeader")}
        </MessageStatusListHeader>
      )}
      <RecipientList
        dataTestId="recipient-list"
        recipients={filteredRecipientList}
        selectedUsers={selectedUsers}
        handleUserSelected={handleUserSelected}
        showCheckbox={enableAcknowledgement}
        showMessageStatus={!enableAcknowledgement}
      />
      {isLoading ? (
        <Loader />
      ) : (
        filteredRecipientList.length === 0 && (
          <>{<EmptyRecordView emptyMessage={emptyRecordMessage} />}</>
        )
      )}
    </MessageItemModalContentWrapper>
  );
};

export default MessageDetail;
